// src/components/common/vibe/EditPoll.js
import React, { useState } from 'react';
import { X, Plus, Trash2, Calendar } from 'lucide-react';
import vibeService from '../../../services/vibe.service';

const EditPoll = ({ isOpen, onClose, poll, onPollUpdated, theme }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        poll_title: poll?.poll_title || '',
        poll_description: poll?.poll_description || '',
        poll_question: poll?.poll_question || '',
        poll_options: poll?.poll_options ?
            (typeof poll.poll_options === 'string' ? JSON.parse(poll.poll_options) : poll.poll_options)
                .map(opt => opt.text || opt) : ['', ''],
        poll_end_date: poll?.poll_end_date ?
            new Date(poll.poll_end_date).toISOString().slice(0, 16) : ''
    });

    const handleAddOption = () => {
        if (formData.poll_options.length < 10) {
            setFormData({
                ...formData,
                poll_options: [...formData.poll_options, '']
            });
        }
    };

    const handleRemoveOption = (index) => {
        if (formData.poll_options.length > 2) {
            const newOptions = formData.poll_options.filter((_, i) => i !== index);
            setFormData({ ...formData, poll_options: newOptions });
        }
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.poll_options];
        newOptions[index] = value;
        setFormData({ ...formData, poll_options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // //console.log('✏️ Updating poll...', {
        //     pollId: poll.id,
        //     title: formData.poll_title
        // });

        // Validation
        if (!formData.poll_title.trim() || !formData.poll_question.trim()) {
            alert('Please provide a title and question for the poll.');
            return;
        }

        const validOptions = formData.poll_options.filter(opt => opt.trim() !== '');
        if (validOptions.length < 2) {
            alert('Please provide at least 2 poll options.');
            return;
        }

        setLoading(true);
        try {
            const response = await vibeService.updatePoll(poll.id, {
                poll_title: formData.poll_title.trim(),
                poll_description: formData.poll_description.trim() || null,
                poll_question: formData.poll_question.trim(),
                poll_options: validOptions,
                poll_end_date: formData.poll_end_date || null
            });

            //console.log('✅ Poll updated successfully');

            // Show different message based on whether votes were cleared
            if (response.votes_cleared) {
                alert(`✅ Poll "${formData.poll_title}" updated successfully!\n\n⚠️ All previous votes have been cleared because poll options were changed.`);
            } else {
                alert(`✅ Poll "${formData.poll_title}" updated successfully!`);
            }

            if (onPollUpdated) {
                await onPollUpdated();
            }

            onClose();
        } catch (error) {
            console.error('❌ Error updating poll:', error);
            alert(`❌ Failed to update poll: ${error.response?.data?.message || error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl p-6 m-4 ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className={`text-2xl font-bold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        ✏️ Edit Poll
                    </h2>
                    <button
                        onClick={onClose}
                        className={`p-2 rounded-lg ${
                            theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.poll_title}
                            onChange={(e) => setFormData({ ...formData, poll_title: e.target.value })}
                            placeholder="e.g., Team Lunch Preference"
                            maxLength={200}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Description (Optional)
                        </label>
                        <textarea
                            value={formData.poll_description}
                            onChange={(e) => setFormData({ ...formData, poll_description: e.target.value })}
                            placeholder="Brief description of what this poll is about..."
                            rows={2}
                            className={`w-full px-4 py-2 rounded-lg border resize-none ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Question */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Question <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.poll_question}
                            onChange={(e) => setFormData({ ...formData, poll_question: e.target.value })}
                            placeholder="e.g., What type of food would you like?"
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Poll Options */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            Poll Options (2-10) <span className="text-red-500">*</span>
                        </label>
                        <p className={`text-xs mb-2 ${
                            theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                        }`}>
                            ⚠️ Warning: Changing options will reset all existing votes
                        </p>
                        <div className="space-y-2">
                            {formData.poll_options.map((option, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => handleOptionChange(index, e.target.value)}
                                        placeholder={`Option ${index + 1}`}
                                        className={`flex-1 px-4 py-2 rounded-lg border ${
                                            theme === 'dark'
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                        }`}
                                    />
                                    {formData.poll_options.length > 2 && (
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveOption(index)}
                                            className={`p-2 rounded-lg text-red-500 ${
                                                theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                                            }`}
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {formData.poll_options.length < 10 && (
                            <button
                                type="button"
                                onClick={handleAddOption}
                                className={`mt-2 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                                    theme === 'dark'
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <Plus className="w-4 h-4" />
                                Add Option
                            </button>
                        )}
                    </div>

                    {/* End Date */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                            <Calendar className="w-4 h-4 inline mr-1" />
                            End Date (Optional)
                        </label>
                        <input
                            type="datetime-local"
                            value={formData.poll_end_date}
                            onChange={(e) => setFormData({ ...formData, poll_end_date: e.target.value })}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        />
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium ${
                                theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`flex-1 px-6 py-3 rounded-lg font-medium text-white ${
                                loading
                                    ? 'bg-blue-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                        >
                            {loading ? 'Updating...' : 'Update Poll'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditPoll;
